import React, { useState, useEffect } from 'react';

import { SafeRichText } from '@/components/SafeRichText/SafeRichText';
import { useOnUploadFileHook } from '../../../hooks/useOnUploadFileHook';
import {
  EventDefaultLogistics,
  EventType,
  RequestUploadUrlType,
} from '../../../graphql/generatedTypes';
import {
  GetEventDocument,
  useGetEventQuery,
} from '../../../graphql/queries/getEvent.generated';
import { useUpdateEventMutation } from '../../../graphql/mutations/updateEvent.generated';
import moment from 'moment';

import { useHistory } from 'react-router';
import { LayoutStoreInstance } from '../../../LayoutStore';
import LabeledCheckBox from '../../../components/LabeledCheckBox';
import i18n from '@/utils/i18n';
import { GetEventInvoicingReadinessDocument } from '../../../graphql/queries/getEventInvoicingReadiness.generated';
import { Routes } from '@/utils/routes';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import '../../../tools/DateTimeHelpers';

interface EditEventFormProps {
  eventId: string;
}

const DEFAULT_LOGISTICS_OPTIONS: Array<{
  value: EventDefaultLogistics;
  labelKey: string;
}> = [
  {
    value: EventDefaultLogistics.Pallet,
    labelKey: 'Event:defaultLogisticsPallet',
  },
  {
    value: EventDefaultLogistics.Parcel,
    labelKey: 'Event:defaultLogisticsParcel',
  },
];

// Saves on change, independent of the form's Save button: Zoho-synced events
// render read-only (everything else on them comes from the CRM), but this
// setting is ours and must stay editable on every event.
const DefaultLogisticsSelect: React.FC<{
  eventId: string;
  value: EventDefaultLogistics | undefined;
  onSaved?: () => void;
}> = ({ eventId, value, onSaved }) => {
  const [updateEvent, { loading }] = useUpdateEventMutation({
    refetchQueries: [GetEventDocument],
    onCompleted: () => {
      LayoutStoreInstance.setSuccess(i18n.t('Event:defaultLogisticsSaved'));
      onSaved?.();
    },
    onError: (error) => {
      LayoutStoreInstance.setError(i18n.t('Event:savedEventError') + error);
    },
  });
  return (
    <div>
      <select
        id="eventDefaultLogistics"
        className="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-primary"
        value={value ?? EventDefaultLogistics.Pallet}
        disabled={loading}
        onChange={(e) =>
          updateEvent({
            variables: {
              event_id: eventId,
              input: {
                default_logistics: e.target.value as EventDefaultLogistics,
              },
            },
          })
        }
      >
        {DEFAULT_LOGISTICS_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {i18n.t(option.labelKey)}
          </option>
        ))}
      </select>
      <p className="tw:mt-1 tw:text-xs tw:text-gray-500">
        {i18n.t('Event:defaultLogisticsHint')}
      </p>
    </div>
  );
};

// Read-only: the value is synced from the Zoho CRM Deal (Minimum_Cleaning_fee_rate)
const MinimumCleaningFeeRateValue: React.FC<{
  rate: number | null | undefined;
}> = ({ rate }) =>
  rate != null ? (
    <span>{rate}%</span>
  ) : (
    <span className="tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:rounded tw:bg-yellow-50 tw:border tw:border-yellow-200 tw:text-yellow-800">
      {i18n.t('Event:minimumCleaningFeeRateNone')}
    </span>
  );

const EditEventForm: React.FC<EditEventFormProps> = ({ eventId }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [externalZohoId, setExternalZohoId] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [type, setType] = useState<EventType>(EventType.Default);
  const [isAuthorizedSignerNecessary, setIsAuthorizedSignerNecessary] =
    useState(false);

  const { data: eventData, loading: queryLoading } = useGetEventQuery({
    variables: { id: eventId },
  });
  const [updateEvent, { error }] = useUpdateEventMutation({
    refetchQueries: [GetEventDocument, GetEventInvoicingReadinessDocument],
    onCompleted: () => {
      LayoutStoreInstance.setSuccess(i18n.t('Event:savedEvent'));
    },
    onError: (error) => {
      LayoutStoreInstance.setError(i18n.t('Event:savedEventError') + error);
    },
  });
  const {
    loading: uploadLoading,
    getUrlForS3FileUploadOnFileInputChangeEvent,
  } = useOnUploadFileHook(RequestUploadUrlType.EventImage);

  const history = useHistory();

  useEffect(() => {
    if (eventData?.event) {
      setName(eventData.event.name || '');
      setDescription(eventData.event.description || '');
      setStartDate(eventData.event.start_date || '');
      setEndDate(eventData.event.end_date || '');
      setImageUrl(eventData.event.image_url || '');
      setExternalZohoId(eventData.event.external_zoho_id || '');
      setIsAuthorizedSignerNecessary(
        eventData.event.is_authorized_signer_necessary,
      );
      setType(eventData.event.type || EventType.Default);
    }
  }, [eventData]);

  const handleUpdateEvent = async () => {
    if (!name || !description || !startDate || !endDate) {
      alert('Name, description, start date and end date are required');
      return;
    }
    const updateResult = await updateEvent({
      variables: {
        event_id: eventId,
        input: {
          name,
          description,
          image_url: imageUrl || undefined,
          start_date: moment(startDate).toISOString(),
          end_date: moment(endDate).toISOString(),
          is_authorized_signer_necessary: isAuthorizedSignerNecessary,
          type,
        },
      },
    });
    if (updateResult.data?.updateEvent?.id) {
      history.push(Routes.eventDetail(updateResult.data.updateEvent.id));
    }

    if (updateResult.errors) {
      LayoutStoreInstance.setError('Error updating event');
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = await getUrlForS3FileUploadOnFileInputChangeEvent(e);
    if (url) {
      setImageUrl(url);
    }
  };

  if (queryLoading) return <p>{i18n.t('Common:loading')}</p>;

  return (
    <div className="tw:space-y-6">
      <div className="tw:flex tw:justify-between tw:items-center tw:pb-4 tw:border-b tw:border-gray-200">
        <h3 className="tw:text-xl tw:font-semibold">
          {i18n.t('Event:generalInfo')}
        </h3>
        {!externalZohoId && (
          <Button onClick={handleUpdateEvent}>{i18n.t('Common:save')}</Button>
        )}
      </div>
      <div>
        {externalZohoId ? (
          // Key-value table display for Zoho events
          <div className="tw:bg-transparent">
            <table className="tw:w-full tw:text-sm">
              <tbody className="tw:divide-y tw:divide-gray-100">
                <tr>
                  <td className="tw:py-2 tw:pr-4 tw:font-semibold tw:text-gray-700 tw:whitespace-nowrap tw:align-top tw:w-[220px]">
                    {i18n.t('Common:name')}
                  </td>
                  <td className="tw:py-2">{name}</td>
                </tr>
                {description && (
                  <tr>
                    <td className="tw:py-2 tw:pr-4 tw:font-semibold tw:text-gray-700 tw:whitespace-nowrap tw:align-top tw:w-[220px]">
                      {i18n.t('Common:description')}
                    </td>
                    <td className="tw:py-2">{description}</td>
                  </tr>
                )}
                <tr>
                  <td className="tw:py-2 tw:pr-4 tw:font-semibold tw:text-gray-700 tw:whitespace-nowrap tw:align-top tw:w-[220px]">
                    {i18n.t('Event:type')}
                  </td>
                  <td className="tw:py-2">
                    <span className="tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:rounded tw:bg-blue-500 tw:text-white">
                      {i18n.t(
                        `Event:type${Object.keys(EventType).find(
                          (key) =>
                            EventType[key as keyof typeof EventType] === type,
                        )}`,
                      )}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="tw:py-2 tw:pr-4 tw:font-semibold tw:text-gray-700 tw:whitespace-nowrap tw:align-top tw:w-[220px]">
                    {i18n.t('Event:mainContractingEventAccount')}
                  </td>
                  <td className="tw:py-2">
                    {eventData?.event?.main_contracting_event_account ? (
                      <Link
                        to={Routes.eventAccountDetail(
                          eventData.event.main_contracting_event_account.id,
                        )}
                        className="tw:text-primary tw:hover:underline"
                      >
                        {eventData.event.main_contracting_event_account.name}
                      </Link>
                    ) : (
                      <span className="tw:text-gray-500">
                        {i18n.t('Common:notSet')}
                      </span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="tw:py-2 tw:pr-4 tw:font-semibold tw:text-gray-700 tw:whitespace-nowrap tw:align-top tw:w-[220px]">
                    {i18n.t('Common:startDate')}
                  </td>
                  <td className="tw:py-2">
                    {moment(startDate).format('DD.MM.YYYY')}
                  </td>
                </tr>
                <tr>
                  <td className="tw:py-2 tw:pr-4 tw:font-semibold tw:text-gray-700 tw:whitespace-nowrap tw:align-top tw:w-[220px]">
                    {i18n.t('Common:endDate')}
                  </td>
                  <td className="tw:py-2">
                    {moment(endDate).format('DD.MM.YYYY')}
                  </td>
                </tr>
                <tr>
                  <td className="tw:py-2 tw:pr-4 tw:font-semibold tw:text-gray-700 tw:whitespace-nowrap tw:align-top tw:w-[220px]">
                    {i18n.t('Event:carrierPickupDate')}
                  </td>
                  <td className="tw:py-2">
                    {eventData?.event?.carrier_pickup_date ? (
                      <span>
                        {moment(eventData.event.carrier_pickup_date).format(
                          'DD.MM.YYYY',
                        )}
                      </span>
                    ) : (
                      <span className="tw:text-gray-500">
                        {i18n.t('Common:notSet')}
                      </span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="tw:py-2 tw:pr-4 tw:font-semibold tw:text-gray-700 tw:whitespace-nowrap tw:align-top tw:w-[220px]">
                    {i18n.t('Event:pickupDateAfterEvent')}
                  </td>
                  <td className="tw:py-2">
                    {eventData?.event?.pickup_date_after_event ? (
                      <span>
                        {moment(eventData.event.pickup_date_after_event).format(
                          'DD.MM.YYYY',
                        )}
                      </span>
                    ) : (
                      <span className="tw:text-gray-500">
                        {i18n.t('Common:notSet')}
                      </span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="tw:py-2 tw:pr-4 tw:font-semibold tw:text-gray-700 tw:whitespace-nowrap tw:align-top tw:w-[220px]">
                    {i18n.t('Event:defaultLogistics')}
                  </td>
                  <td className="tw:py-2">
                    <DefaultLogisticsSelect
                      eventId={eventId}
                      value={eventData?.event?.default_logistics}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="tw:py-2 tw:pr-4 tw:font-semibold tw:text-gray-700 tw:whitespace-nowrap tw:align-top tw:w-[220px]">
                    {i18n.t('Event:minimumCleaningFeeRate')}
                  </td>
                  <td className="tw:py-2">
                    <MinimumCleaningFeeRateValue
                      rate={eventData?.event?.minimum_cleaning_fee_rate}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="tw:py-2 tw:pr-4 tw:font-semibold tw:text-gray-700 tw:whitespace-nowrap tw:align-top tw:w-[220px]">
                    {i18n.t('EventAccountEditPage:isAuthorizedSignerNecessary')}
                  </td>
                  <td className="tw:py-2">
                    <span
                      className={`tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:rounded ${
                        isAuthorizedSignerNecessary
                          ? 'tw:bg-green-500 tw:text-white'
                          : 'tw:bg-gray-500 tw:text-white'
                      }`}
                    >
                      {isAuthorizedSignerNecessary
                        ? i18n.t('Common:yes')
                        : i18n.t('Common:no')}
                    </span>
                  </td>
                </tr>
                {imageUrl && (
                  <tr>
                    <td className="tw:py-2 tw:pr-4 tw:font-semibold tw:text-gray-700 tw:whitespace-nowrap tw:align-top tw:w-[220px]">
                      {i18n.t('EventAccountEditPage:image')}
                    </td>
                    <td className="tw:py-2">
                      <img
                        src={imageUrl}
                        alt="Event"
                        className="tw:max-w-[200px] tw:rounded tw:border tw:border-gray-300"
                      />
                    </td>
                  </tr>
                )}
                <tr>
                  <td className="tw:py-2 tw:pr-4 tw:font-semibold tw:text-gray-700 tw:whitespace-nowrap tw:align-top tw:w-[220px]">
                    {i18n.t('Event:merchantOrderFormDeadline')}
                  </td>
                  <td className="tw:py-2">
                    {eventData?.event?.merchant_order_form_deadline ? (
                      <span>
                        {eventData.event.merchant_order_form_deadline.toDateString(
                          true,
                        )}
                      </span>
                    ) : (
                      <span className="tw:text-gray-500">
                        {i18n.t('Common:notSet')}
                      </span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="tw:py-2 tw:pr-4 tw:font-semibold tw:text-gray-700 tw:whitespace-nowrap tw:align-top tw:w-[220px]">
                    {i18n.t('Event:merchantOrderFormInfoText')}
                  </td>
                  <td className="tw:py-2">
                    {eventData?.event?.merchant_order_form_info_text ? (
                      <div className="tw:rounded-xl tw:border tw:bg-card tw:text-card-foreground tw:shadow tw:p-4">
                        <SafeRichText
                          html={eventData.event.merchant_order_form_info_text}
                        />
                      </div>
                    ) : (
                      <span className="tw:text-gray-500">
                        {i18n.t('Common:notSet')}
                      </span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          // Editable form for non-Zoho synced events
          <form className="tw:clear-both tw:space-y-4">
            <div>
              <label
                htmlFor="eventName"
                className="tw:block tw:mb-2 tw:text-sm tw:font-medium"
              >
                {i18n.t('Common:name')}
              </label>
              <input
                type="text"
                name="name"
                id="eventName"
                placeholder="Event Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-primary"
              />
            </div>
            <div>
              <label
                htmlFor="eventDescription"
                className="tw:block tw:mb-2 tw:text-sm tw:font-medium"
              >
                {i18n.t('Common:description')}
              </label>
              <textarea
                name="description"
                id="eventDescription"
                placeholder="Event Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-primary"
                rows={4}
              />
            </div>
            <div>
              <label
                htmlFor="eventType"
                className="tw:block tw:mb-2 tw:text-sm tw:font-medium"
              >
                {i18n.t('Event:type')}
              </label>
              <select
                id="eventType"
                className="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-primary"
                onChange={(e) => setType(e.target.value as any)}
                value={type || ''}
              >
                {Object.entries(EventType)
                  .filter(
                    ([, value]) =>
                      (value !== EventType.Default &&
                        value !== EventType.DeliveryOnlyFullEvent) ||
                      type === value,
                  )
                  .map(([key, value]) => (
                    <option key={key} value={value}>
                      {i18n.t(`Event:type${key}`)}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="tw:block tw:mb-2 tw:text-sm tw:font-medium">
                {i18n.t('Event:mainContractingEventAccount')}
              </label>
              <div className="tw:flex">
                <input
                  type="text"
                  value={
                    eventData?.event?.main_contracting_event_account
                      ? eventData.event.main_contracting_event_account.name ||
                        ''
                      : i18n.t('Common:notSet')
                  }
                  disabled
                  className="tw:flex-1 tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md"
                />
              </div>
              {eventData?.event?.main_contracting_event_account && (
                <div className="tw:mt-1">
                  <Link
                    to={Routes.eventAccountDetail(
                      eventData.event.main_contracting_event_account.id,
                    )}
                    className="tw:text-primary tw:hover:underline"
                  >
                    {i18n.t('Common:viewDetails')}
                  </Link>
                </div>
              )}
            </div>
            <div>
              <label
                htmlFor="eventStartDate"
                className="tw:block tw:mb-2 tw:text-sm tw:font-medium"
              >
                {i18n.t('Common:startDate')}
              </label>
              <input
                type="date"
                value={moment(startDate).format('YYYY-MM-DD')}
                name="event-start-date"
                id="eventStartDate"
                onChange={(e) => {
                  console.log(e.target.value);
                  const newDate = new Date(e.target.value);
                  setStartDate(moment(newDate).format('YYYY-MM-DD'));
                }}
                className="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-primary"
              />
            </div>
            <div>
              <label
                htmlFor="eventEndDate"
                className="tw:block tw:mb-2 tw:text-sm tw:font-medium"
              >
                {i18n.t('Common:endDate')}
              </label>
              <input
                type="date"
                value={moment(endDate).format('YYYY-MM-DD')}
                name="event-end-date"
                id="eventEndDate"
                onChange={(e) => {
                  console.log(e.target.value);
                  const newDate = new Date(e.target.value);
                  setEndDate(moment(newDate).format('YYYY-MM-DD'));
                }}
                className="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-primary"
              />
            </div>
            <div>
              <label className="tw:block tw:mb-2 tw:text-sm tw:font-medium">
                {i18n.t('Event:carrierPickupDate')}
              </label>
              <div className="tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:bg-gray-50">
                {eventData?.event?.carrier_pickup_date ? (
                  moment(eventData.event.carrier_pickup_date).format(
                    'DD.MM.YYYY',
                  )
                ) : (
                  <span className="tw:text-gray-500">
                    {i18n.t('Common:notSet')}
                  </span>
                )}
              </div>
            </div>
            <div>
              <label className="tw:block tw:mb-2 tw:text-sm tw:font-medium">
                {i18n.t('Event:pickupDateAfterEvent')}
              </label>
              <div className="tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:bg-gray-50">
                {eventData?.event?.pickup_date_after_event ? (
                  moment(eventData.event.pickup_date_after_event).format(
                    'DD.MM.YYYY',
                  )
                ) : (
                  <span className="tw:text-gray-500">
                    {i18n.t('Common:notSet')}
                  </span>
                )}
              </div>
            </div>
            <div>
              <label
                htmlFor="eventDefaultLogistics"
                className="tw:block tw:mb-2 tw:text-sm tw:font-medium"
              >
                {i18n.t('Event:defaultLogistics')}
              </label>
              <DefaultLogisticsSelect
                eventId={eventId}
                value={eventData?.event?.default_logistics}
              />
            </div>
            <div>
              <label
                htmlFor="eventImage"
                className="tw:block tw:mb-2 tw:text-sm tw:font-medium"
              >
                {i18n.t('EventAccountEditPage:image')}
              </label>
              <input
                type="file"
                name="image"
                id="eventImage"
                onChange={handleImageUpload}
                className="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-primary"
              />
              {uploadLoading && (
                <p className="tw:mt-1 tw:text-sm tw:text-gray-500">
                  {i18n.t('EventAccountEditPage:uploadingImage')}
                </p>
              )}
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt="Event"
                  className="tw:max-w-[200px] tw:mt-2.5"
                />
              )}
            </div>
            <div className="tw:mb-2">
              <LabeledCheckBox
                checked={isAuthorizedSignerNecessary}
                onClick={() =>
                  setIsAuthorizedSignerNecessary(!isAuthorizedSignerNecessary)
                }
              >
                {i18n.t('EventAccountEditPage:isAuthorizedSignerNecessary')}
              </LabeledCheckBox>
            </div>
            <div>
              <label className="tw:block tw:mb-2 tw:text-sm tw:font-medium">
                {i18n.t('Event:minimumCleaningFeeRate')}
              </label>
              <div className="tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:bg-gray-50">
                <MinimumCleaningFeeRateValue
                  rate={eventData?.event?.minimum_cleaning_fee_rate}
                />
              </div>
            </div>
            <div>
              <label className="tw:block tw:mb-2 tw:text-sm tw:font-medium">
                {i18n.t('Event:merchantOrderFormDeadline')}
              </label>
              <div className="tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:bg-gray-50">
                {eventData?.event?.merchant_order_form_deadline ? (
                  eventData.event.merchant_order_form_deadline.toDateString(
                    true,
                  )
                ) : (
                  <span className="tw:text-gray-500">
                    {i18n.t('Common:notSet')}
                  </span>
                )}
              </div>
            </div>
            <div>
              <label className="tw:block tw:mb-2 tw:text-sm tw:font-medium">
                {i18n.t('Event:merchantOrderFormInfoText')}
              </label>
              <div className="tw:px-3 tw:py-2 tw:border tw:border-gray-300 tw:rounded-md tw:bg-gray-50">
                {eventData?.event?.merchant_order_form_info_text ? (
                  <SafeRichText
                    html={eventData.event.merchant_order_form_info_text}
                    className="tw:m-0"
                  />
                ) : (
                  <span className="tw:text-gray-500">
                    {i18n.t('Common:notSet')}
                  </span>
                )}
              </div>
            </div>
            {error && (
              <div className="tw:p-3 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-md tw:text-red-700">
                {i18n.t('Common:error')}: {error.message}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default EditEventForm;
